/**
 * AccessibilityBar — a senior-friendly command bar mounted on every
 * SocialCapital page. It combines three power-features that used to be
 * scattered across bottom-left corners and per-block widgets:
 *
 *   1. Listen to this page — reads the visible <main> content aloud
 *      with the browser's SpeechSynthesis engine (works offline, free,
 *      supports 30+ languages if the OS has voice packs installed).
 *   2. Text size — quick S / M / L / XL toggle (wraps the existing
 *      data-font-scale attribute already understood by index.css).
 *   3. Translate this page — dropdown of 55+ languages. Broadcasts the
 *      existing `translate-page` event so every <TranslatableText />
 *      picks it up, AND writes the `sp:page-translate-lang` key so new
 *      routes auto-translate on mount.
 *
 * Design goals:
 *   - Large touch targets (44px min), high contrast, big labels — no
 *     tiny icon-only buttons. Seniors should be able to read every CTA
 *     without hunting.
 *   - Sticky at the top so users can access it anywhere on the page.
 *   - No dependency on user auth or route context; purely client-side.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Volume2, VolumeX, Type, Languages, ChevronDown, Loader2 } from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";

const FONT_KEY = "sp.font-scale";
const LANG_KEY = "sp:page-translate-lang";
const PAGE_TRANSLATE_EVENT = "translate-page";
const PAGE_REVERT_EVENT = "revert-page-translation";

// Canonical curated language list — same 30+ codes the LLM endpoint
// supports on the backend. Native labels help non-English speakers find
// their own language.
const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "hi", label: "हिन्दी" },
  { code: "ta", label: "தமிழ்" },
  { code: "ml", label: "മലയാളം" },
  { code: "te", label: "తెలుగు" },
  { code: "kn", label: "ಕನ್ನಡ" },
  { code: "mr", label: "मराठी" },
  { code: "bn", label: "বাংলা" },
  { code: "gu", label: "ગુજરાતી" },
  { code: "pa", label: "ਪੰਜਾਬੀ" },
  { code: "ur", label: "اردو" },
  { code: "ar", label: "العربية" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "pt", label: "Português" },
  { code: "it", label: "Italiano" },
  { code: "ru", label: "Русский" },
  { code: "zh", label: "中文 (简体)" },
  { code: "ja", label: "日本語" },
  { code: "ko", label: "한국어" },
  { code: "nl", label: "Nederlands" },
  { code: "tr", label: "Türkçe" },
  { code: "vi", label: "Tiếng Việt" },
  { code: "th", label: "ภาษาไทย" },
  { code: "id", label: "Bahasa Indonesia" },
  { code: "sw", label: "Kiswahili" },
  { code: "he", label: "עברית" },
  { code: "fa", label: "فارسی" },
  { code: "pl", label: "Polski" },
  { code: "uk", label: "Українська" },
  { code: "el", label: "Ελληνικά" },
  { code: "ne", label: "नेपाली" },
];

const FONT_LEVELS = [
  { level: "2", label: "A",   title: "Standard" },
  { level: "3", label: "A+",  title: "Larger"   },
  { level: "4", label: "A++", title: "Extra large" },
];

// Gather text from the current page's main content region for TTS.
// We prefer <main>, fall back to <body>. We skip navigation, footer,
// script, style, and hidden nodes.
function collectPageText() {
  if (typeof document === "undefined") return "";
  const root = document.querySelector("main") || document.body;
  if (!root) return "";
  const skipTags = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "NAV", "FOOTER", "SVG"]);
  const out = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(n) {
      const p = n.parentElement;
      if (!p) return NodeFilter.FILTER_REJECT;
      if (skipTags.has(p.tagName)) return NodeFilter.FILTER_REJECT;
      // Skip the accessibility bar itself.
      if (p.closest("[data-testid='accessibility-bar']")) return NodeFilter.FILTER_REJECT;
      // Skip hidden.
      const s = window.getComputedStyle(p);
      if (s.display === "none" || s.visibility === "hidden") return NodeFilter.FILTER_REJECT;
      const t = (n.textContent || "").trim();
      if (!t) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let node;
  // eslint-disable-next-line no-cond-assign
  while ((node = walker.nextNode())) {
    out.push(node.textContent.trim());
    if (out.length > 800) break; // safety cap
  }
  return out.join(". ").replace(/\s+/g, " ").slice(0, 8000);
}

export default function AccessibilityBar() {
  // ---- Font scale ----
  const [fontLevel, setFontLevel] = useState("3");
  useEffect(() => {
    try {
      const stored = localStorage.getItem(FONT_KEY) || "3";
      setFontLevel(stored);
      document.documentElement.setAttribute("data-font-scale", stored);
    } catch { /* ignore */ }
  }, []);
  const setFont = (v) => {
    setFontLevel(v);
    document.documentElement.setAttribute("data-font-scale", v);
    try { localStorage.setItem(FONT_KEY, v); } catch { /* ignore */ }
  };

  // ---- Language / translate ----
  const [lang, setLang] = useState("en");
  const [langOpen, setLangOpen] = useState(false);
  const [translating, setTranslating] = useState(false);
  const langMenuRef = useRef(null);
  useEffect(() => {
    try {
      const stored = localStorage.getItem(LANG_KEY) || "en";
      setLang(stored);
    } catch { /* ignore */ }
  }, []);
  // Close dropdown on outside click.
  useEffect(() => {
    if (!langOpen) return undefined;
    const onDown = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [langOpen]);

  const pickLang = useCallback(async (code) => {
    setLang(code);
    setLangOpen(false);
    try { localStorage.setItem(LANG_KEY, code); } catch { /* ignore */ }
    if (code === "en") {
      window.dispatchEvent(new CustomEvent(PAGE_REVERT_EVENT));
      // Revert Google Translate cookie if the widget set one.
      try { document.cookie = "googtrans=; path=/; max-age=0"; } catch {}
      return;
    }
    setTranslating(true);
    window.dispatchEvent(new CustomEvent(PAGE_TRANSLATE_EVENT, { detail: { lang: code } }));
    // Give TranslatableText blocks a moment to fetch, then release the
    // spinner — the actual per-block state has its own indicator.
    setTimeout(() => setTranslating(false), 1200);
  }, []);

  const currentLangLabel = useMemo(
    () => LANGUAGES.find((l) => l.code === lang)?.label || "English",
    [lang],
  );

  // ---- Listen to page (TTS) ----
  const ttsSupported = typeof window !== "undefined" && "speechSynthesis" in window;
  const [speaking, setSpeaking] = useState(false);
  const utteranceRef = useRef(null);
  const stopSpeak = useCallback(() => {
    if (!ttsSupported) return;
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    setSpeaking(false);
  }, [ttsSupported]);
  const pickVoice = (targetLang) => {
    if (!ttsSupported) return null;
    const voices = window.speechSynthesis.getVoices() || [];
    if (!voices.length) return null;
    const short = (targetLang || "").toLowerCase().split("-")[0];
    return (
      voices.find((v) => v.lang && v.lang.toLowerCase().startsWith(short + "-")) ||
      voices.find((v) => v.lang && v.lang.toLowerCase() === short) ||
      null
    );
  };
  const listen = useCallback(async () => {
    if (!ttsSupported) {
      toast.error("Your browser doesn't support text-to-speech.");
      return;
    }
    if (speaking) {
      stopSpeak();
      return;
    }
    let text = collectPageText();
    if (!text) {
      toast.error("No readable text on this page yet.");
      return;
    }
    // If the visitor picked a non-English language, translate the whole
    // page's text before speaking so the voice matches what they read.
    if (lang && lang !== "en") {
      try {
        const { data } = await http.post("/i18n/translate", { text: text.slice(0, 4000), target_lang: lang });
        if (data?.translated) text = data.translated;
      } catch {
        // Silent — fall back to English text with target-language voice.
      }
    }
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    // SpeechSynthesis has a per-utterance length limit on some browsers;
    // split into chunks of ~300 chars at sentence boundaries.
    const chunks = [];
    let buf = "";
    for (const sent of text.split(/(?<=[.!?])\s+/)) {
      if ((buf + " " + sent).length > 300) {
        if (buf) chunks.push(buf);
        buf = sent;
      } else {
        buf = buf ? buf + " " + sent : sent;
      }
    }
    if (buf) chunks.push(buf);

    setSpeaking(true);
    let i = 0;
    const speakNext = () => {
      if (i >= chunks.length) { setSpeaking(false); return; }
      const u = new SpeechSynthesisUtterance(chunks[i++]);
      u.lang = lang || "en";
      u.rate = 0.95;
      u.pitch = 1;
      const v = pickVoice(u.lang);
      if (v) u.voice = v;
      u.onend = speakNext;
      u.onerror = () => setSpeaking(false);
      utteranceRef.current = u;
      try { window.speechSynthesis.speak(u); } catch { setSpeaking(false); }
    };
    speakNext();
  }, [ttsSupported, speaking, stopSpeak, lang]);

  // Warm voice list (Chrome loads async) and cancel speech on unmount.
  useEffect(() => {
    if (!ttsSupported) return undefined;
    const ping = () => window.speechSynthesis.getVoices();
    ping();
    window.speechSynthesis.addEventListener?.("voiceschanged", ping);
    return () => {
      try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
      window.speechSynthesis.removeEventListener?.("voiceschanged", ping);
    };
  }, [ttsSupported]);

  return (
    <div
      data-testid="accessibility-bar"
      className="w-full border-b sticky top-0 z-30"
      style={{
        background: "linear-gradient(90deg, #0F172A 0%, #1E293B 50%, #0F172A 100%)",
        color: "#F8FAFC",
        borderColor: "rgba(255,255,255,0.08)",
      }}
    >
      <div className="max-w-[1400px] mx-auto flex flex-wrap items-center gap-3 px-4 sm:px-6 py-2.5">
        {/* Listen to page */}
        <button
          type="button"
          onClick={listen}
          data-testid="ab-listen"
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-semibold transition-colors text-base"
          style={{
            background: speaking ? "#F97316" : "#FDBA74",
            color: "#0F172A",
            minHeight: 44,
          }}
          aria-label={speaking ? "Stop reading this page" : "Listen to this page"}
        >
          {speaking ? <VolumeX className="w-5 h-5" aria-hidden /> : <Volume2 className="w-5 h-5" aria-hidden />}
          <span>{speaking ? "Stop" : "Listen to this page"}</span>
        </button>

        {/* Font size */}
        <div
          className="inline-flex items-center gap-1 rounded-full px-2 py-1"
          style={{ background: "rgba(255,255,255,0.08)", minHeight: 44 }}
          data-testid="ab-font-group"
        >
          <Type className="w-4 h-4 opacity-70 ml-1" aria-hidden />
          <span className="text-sm opacity-80 mr-1 hidden sm:inline">Text size</span>
          {FONT_LEVELS.map((f) => {
            const active = fontLevel === f.level;
            return (
              <button
                key={f.level}
                type="button"
                onClick={() => setFont(f.level)}
                title={f.title}
                data-testid={`ab-font-${f.level}`}
                className={
                  "px-3 py-1.5 rounded-full font-semibold transition " +
                  (active ? "text-slate-900" : "text-white hover:bg-white/10")
                }
                style={active ? { background: "#FDBA74" } : {}}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {/* Translate */}
        <div className="relative" ref={langMenuRef}>
          <button
            type="button"
            onClick={() => setLangOpen((v) => !v)}
            data-testid="ab-translate-open"
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 font-semibold transition-colors text-base"
            style={{
              background: "rgba(255,255,255,0.10)",
              color: "#F8FAFC",
              minHeight: 44,
            }}
            aria-haspopup="listbox"
            aria-expanded={langOpen}
          >
            {translating ? <Loader2 className="w-5 h-5 animate-spin" aria-hidden /> : <Languages className="w-5 h-5" aria-hidden />}
            <span>Read in: {currentLangLabel}</span>
            <ChevronDown className="w-4 h-4 opacity-80" aria-hidden />
          </button>
          {langOpen && (
            <div
              className="absolute z-40 mt-2 left-0 rounded-xl shadow-2xl border p-2 max-h-[60vh] overflow-y-auto"
              style={{
                background: "#0F172A",
                borderColor: "rgba(255,255,255,0.1)",
                minWidth: 240,
              }}
              role="listbox"
              data-testid="ab-translate-menu"
            >
              {LANGUAGES.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => pickLang(l.code)}
                  data-testid={`ab-lang-${l.code}`}
                  className={
                    "w-full text-left px-3 py-2 rounded-lg text-sm transition " +
                    (l.code === lang ? "font-semibold" : "hover:bg-white/10")
                  }
                  style={l.code === lang ? { background: "#FDBA74", color: "#0F172A" } : { color: "#F8FAFC" }}
                  role="option"
                  aria-selected={l.code === lang}
                >
                  {l.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <span className="ml-auto text-xs sm:text-sm opacity-75 hidden md:inline" data-testid="ab-help">
          Accessibility · Listen · Text size · 30+ languages
        </span>
      </div>
    </div>
  );
}
